import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCents, ORDER_STATUS_LABELS, type OrderSummary, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { useSession } from '../../session';
import { orderTone } from './OrdersPage';
import './orders.css';

/** Aufträge eines Kontakts oder einer Aufgabe, für deren Detailseiten (KON-128, KON-131) */
export function RelatedOrders({ contactId, taskId }: { contactId?: string; taskId?: string }) {
  const { can } = useSession();
  const list = useQuery({
    queryKey: ['orders', { contactId, taskId }],
    queryFn: async () => (await api<Paginated<OrderSummary>>('GET', `/orders${queryString({ contactId, taskId, perPage: 50 })}`)).data,
  });
  if (taskId && list.data?.length === 0) return null;

  return (
    <section className="card" aria-label="Aufträge">
      <div className="section-head">
        <h2>Aufträge</h2>
        {contactId && can('orders.write') && <Link className="btn btn--secondary btn--sm" to={`/auftraege/neu?kontakt=${contactId}`}>Neuer Auftrag</Link>}
      </div>
      {list.isLoading && <p className="muted">Wird geladen …</p>}
      {list.isError && <p className="muted">Die Aufträge konnten nicht geladen werden.</p>}
      {list.data?.length === 0 && <p className="muted">Noch kein Auftrag.</p>}
      {list.data && list.data.length > 0 && (
        <ul className="related-offers">
          {list.data.map((o) => (
            <li key={o.id}>
              <Link to={`/auftraege/${o.id}`} className="strong-link">{o.number}</Link>
              <span className={orderTone(o.status)}>{ORDER_STATUS_LABELS[o.status]}</span>
              <span className="muted small related-offers__subject">{o.subject}</span>
              <span className="num">{o.grossCents === null ? "–" : formatCents(o.grossCents)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Link vom angenommenen Angebot auf seinen Auftrag (KON-126). Ohne Auftrag zeigt er nichts. */
export function OfferOrderLink({ offerId }: { offerId: string }) {
  const list = useQuery({
    queryKey: ['orders', { offerId }],
    queryFn: async () => (await api<Paginated<OrderSummary>>('GET', `/orders${queryString({ offerId })}`)).data,
  });
  const order = list.data?.[0];
  if (!order) return null;
  return (
    <div className="alert alert--ok">
      Daraus ist der Auftrag <Link to={`/auftraege/${order.id}`} className="strong-link">{order.number}</Link> entstanden ({ORDER_STATUS_LABELS[order.status]}).
    </div>
  );
}
